import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { settingsService, Language } from '../../../services';

const Languages: React.FC = () => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    nativeName: '',
    flag: '',
    active: true,
  });

  const fetchLanguages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsService.getLanguages();
      setLanguages(response.data || []);
    } catch (err: any) {
      console.error('Error fetching languages:', err);
      setError(err.message || t('settings.languages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const getStatusChip = (active: string) => {
    return active === 'S' ? (
      <Chip size="small" color="success" label={t('common.active')} icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label={t('common.inactive')} icon={<InactiveIcon />} />
    );
  };

  const filteredLanguages = languages.filter(lang => {
    const matchesSearch = searchTerm === '' ||
      lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lang.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lang.nativeName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && lang.active === 'S') ||
      (statusFilter === 'inactive' && lang.active === 'N');
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: languages.length,
    active: languages.filter(l => l.active === 'S').length,
    inactive: languages.filter(l => l.active === 'N').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (language?: Language) => {
    if (language) {
      setSelectedLanguage(language);
      setFormData({
        code: language.code,
        name: language.name,
        nativeName: language.nativeName || '',
        flag: language.flag || '',
        active: language.active === 'S',
      });
    } else {
      setSelectedLanguage(null);
      setFormData({
        code: '',
        name: '',
        nativeName: '',
        flag: '',
        active: true,
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedLanguage(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (selectedLanguage) {
        await settingsService.updateLanguage(selectedLanguage.id, {
          name: formData.name,
          nativeName: formData.nativeName,
          flag: formData.flag,
          active: formData.active ? 'S' : 'N',
        });
      } else {
        await settingsService.createLanguage({
          code: formData.code,
          name: formData.name,
          nativeName: formData.nativeName,
          flag: formData.flag,
        });
      }
      handleCloseEdit();
      fetchLanguages();
    } catch (err: any) {
      setError(err.message || t('settings.languages.errorSaving'));
    }
  };

  const handleDelete = async (language: Language) => {
    if (!window.confirm(t('settings.languages.confirmDelete', { name: language.name }))) {
      return;
    }

    try {
      setError(null);
      await settingsService.deleteLanguage(language.id);
      fetchLanguages();
    } catch (err: any) {
      setError(err.message || t('settings.languages.errorDeleting'));
    }
  };

  if (loading && languages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('settings.languages.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          {t('settings.languages.newLanguage')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.languages.totalLanguages')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.languages.activeFemale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inactive}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.languages.inactiveFemale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('settings.languages.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="active">{t('settings.banks.activeMale')}</MenuItem>
                <MenuItem value="inactive">{t('settings.banks.inactiveMale')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Languages Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={80}></TableCell>
                <TableCell width={80}>{t('common.code')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('settings.languages.nativeName')}</TableCell>
                <TableCell width={100}>{t('common.status')}</TableCell>
                <TableCell width={120} align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLanguages
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((language) => (
                <TableRow key={language.id} hover>
                  <TableCell>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '1.5rem' }}>
                      {language.flag || language.code.substring(0, 2).toUpperCase()}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                      {language.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {language.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {language.nativeName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(language.active)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(language)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(language)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredLanguages.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon />
          {selectedLanguage ? t('settings.languages.editLanguage') : t('settings.languages.newLanguage')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('settings.languages.isoCode')}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
                inputProps={{ maxLength: 5 }}
                helperText={t('settings.languages.isoCodeHelper')}
                disabled={!!selectedLanguage}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('settings.languages.emojiFlag')}
                value={formData.flag}
                onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
                inputProps={{ maxLength: 4 }}
                helperText={t('settings.languages.emojiFlagHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.active ? 'active' : 'inactive'}
                onChange={(e) => setFormData({ ...formData, active: e.target.value === 'active' })}
              >
                <MenuItem value="active">{t('common.active')}</MenuItem>
                <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText={t('settings.languages.nameInPortuguese')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.languages.nativeName')}
                value={formData.nativeName}
                onChange={(e) => setFormData({ ...formData, nativeName: e.target.value })}
                helperText={t('settings.languages.nameInOwnLanguage')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.code || !formData.name}
          >
            {selectedLanguage ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Languages;
